import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Save, Trash2, Star, HelpCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/* ---------------- Testimonials editor ---------------- */
function TestimonialsPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/testimonials/all");
      setRows(data);
    } catch {
      toast.error("Could not load testimonials");
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", role: "", quote: "", order: rows.length, published: true });

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.quote.trim()) {
      toast.error("Name and quote are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/testimonials", editing);
      toast.success("Testimonial saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-white">Testimonials</h2>
          <p className="text-sm text-white/60 mt-1">Shown on the Home page in the "Student wins" section.</p>
        </div>
        <Button
          onClick={startNew}
          data-testid="testimonial-new"
          className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New testimonial
        </Button>
      </div>

      {editing && (
        <div data-testid="testimonial-editor" className="rounded-2xl border border-brand-pink/30 bg-brand-pink/5 p-6 mb-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Name</Label>
              <Input
                data-testid="testimonial-name"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className="rounded-full bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Role / location</Label>
              <Input
                data-testid="testimonial-role"
                placeholder="e.g. Co-host · San Antonio, TX"
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                className="rounded-full bg-black/40 border-white/10 text-white"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Quote</Label>
            <Textarea
              data-testid="testimonial-quote"
              rows={4}
              value={editing.quote}
              onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
              className="rounded-2xl bg-black/40 border-white/10 text-white"
            />
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Label className="text-xs uppercase tracking-widest text-white/60">Order</Label>
              <Input
                type="number"
                data-testid="testimonial-order"
                value={editing.order}
                onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })}
                className="w-20 rounded-full bg-black/40 border-white/10 text-white text-center"
              />
            </div>
            <button
              type="button"
              onClick={() => setEditing({ ...editing, published: !editing.published })}
              data-testid="testimonial-toggle-published"
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs ${
                editing.published ? "bg-brand-pink/20 text-brand-pink" : "bg-white/5 text-white/50"
              }`}
            >
              {editing.published ? <><Eye className="w-3 h-3" /> Published</> : <><EyeOff className="w-3 h-3" /> Draft</>}
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-white/70">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="testimonial-save"
              className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((t) => (
          <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="rounded-2xl border border-white/10 bg-black/40 p-5 flex flex-col">
            <div className="flex items-center gap-1 mb-3">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 text-brand-pink" fill="#E91E63" />)}
              {!t.published && <span className="ml-3 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/60 uppercase tracking-widest">Draft</span>}
            </div>
            <p className="text-white/85 leading-relaxed flex-1 mb-4">"{t.quote}"</p>
            <div className="flex items-center justify-between pt-3 border-t border-white/10">
              <div>
                <div className="font-medium text-white">{t.name}</div>
                <div className="text-xs text-white/50">{t.role}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`testimonial-edit-${t.id}`} className="text-white/70 hover:text-white h-8 px-3 text-xs">Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`} className="text-red-300/80 hover:text-red-200 hover:bg-red-500/10 h-8 px-2 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {rows.length === 0 && !editing && (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-white/50">
          No testimonials yet. Click <b className="text-brand-pink">New testimonial</b> to add your first student win.
        </div>
      )}
    </div>
  );
}

/* ---------------- FAQs editor ---------------- */
function FaqsPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/faqs");
      setRows(data);
    } catch { toast.error("Could not load FAQs"); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ question: "", answer: "", order: rows.length });

  const save = async () => {
    if (!editing) return;
    if (!editing.question.trim() || !editing.answer.trim()) {
      toast.error("Question and answer are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/faqs", editing);
      toast.success("FAQ saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-white">Frequently Asked Questions</h2>
          <p className="text-sm text-white/60 mt-1">Shown at the bottom of the Home page.</p>
        </div>
        <Button
          onClick={startNew}
          data-testid="faq-new"
          className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New FAQ
        </Button>
      </div>

      {editing && (
        <div data-testid="faq-editor" className="rounded-2xl border border-brand-pink/30 bg-brand-pink/5 p-6 mb-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Question</Label>
            <Input
              data-testid="faq-question"
              value={editing.question}
              onChange={(e) => setEditing({ ...editing, question: e.target.value })}
              className="rounded-full bg-black/40 border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Answer</Label>
            <Textarea
              data-testid="faq-answer"
              rows={4}
              value={editing.answer}
              onChange={(e) => setEditing({ ...editing, answer: e.target.value })}
              className="rounded-2xl bg-black/40 border-white/10 text-white"
            />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-xs uppercase tracking-widest text-white/60">Order</Label>
            <Input
              type="number"
              data-testid="faq-order"
              value={editing.order}
              onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) || 0 })}
              className="w-20 rounded-full bg-black/40 border-white/10 text-white text-center"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-white/70">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy}
              data-testid="faq-save"
              className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((f) => (
          <div key={f.id} data-testid={`faq-row-${f.id}`} className="rounded-2xl border border-white/10 bg-black/40 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-white mb-2">{f.question}</div>
                <div className="text-sm text-white/70 leading-relaxed">{f.answer}</div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => setEditing(f)} data-testid={`faq-edit-${f.id}`} className="text-white/70 hover:text-white h-8 px-3 text-xs">Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id}`} className="text-red-300/80 hover:text-red-200 hover:bg-red-500/10 h-8 px-2 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {rows.length === 0 && !editing && (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-white/50">
          No FAQs yet.
        </div>
      )}
    </div>
  );
}

export default function AdminHomeContent() {
  const [tab, setTab] = useState("testimonials");
  const TABS = [
    { id: "testimonials", label: "Testimonials", icon: Star },
    { id: "faqs", label: "FAQs", icon: HelpCircle },
  ];
  return (
    <div>
      <div className="mb-8">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-2">Home Page Content</div>
        <h1 className="font-display text-4xl text-white">Testimonials & FAQs</h1>
        <p className="text-white/60 text-sm mt-2 max-w-xl">Edit the social proof and objection-handlers that appear on your home page. No code, no redeploy.</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-home-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition ${active ? "bg-brand-pink text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "testimonials" && <TestimonialsPanel />}
      {tab === "faqs" && <FaqsPanel />}
    </div>
  );
}
